
import { toast } from 'sonner';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3088/v1/api';

export class RateLimitError extends Error {
  retryAfter: number;

  constructor(retryAfter: number) {
    super('Too many requests');
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

function parseRetryAfter(response: Response): number {
  const header = response.headers.get('Retry-After');
  const seconds = header ? Number(header) : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 30;
}

function notifyRateLimited(response: Response): RateLimitError {
  const retryAfter = parseRetryAfter(response);
  toast.error(`Too many requests — try again in ${retryAfter}s`);
  return new RateLimitError(retryAfter);
}

export interface APIResponse<T = any> {
  code: string;
  message: string;
  data?: T;
  error?: string | null;
  page?: number;
  totalNoOfItem?: number;
  perPage?: number;
  orderBy: any;
  skip: number;
  filters: any;
  searchText?: string;
  workgroupId?: number;
}

export interface QueryOptions {

  workgroupId?: number;
  searchText?: string;
  page?: number;
  perPage?: number;
  orderBy?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: any;
}

export interface Pagination {
  totalItems: number;
  page: number;
  perPage: number;
}
export interface PaginatedResponse<T = any> {
  items: T[];
  filters: T[];
  orderBy: T[];
  pagination: Pagination;
}

class ApiClient {
  private baseURL: string;
  public authToken: string | null = null;
  private isRefreshing = false;
  private refreshCallback: (() => Promise<void>) | null = null;
  private failedQueue = [] as any;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  setRefreshCallback(callback: () => Promise<void>) {
    this.refreshCallback = callback;
  }


  subscribeTokenRefresh(cb: any) {
    this.failedQueue.push(cb);
  }

  onRefreshed(token) {
    this.failedQueue.forEach(cb => cb(token));
    this.failedQueue = [];
  }


  private redirectToLogin(): void {
    const currentPath = window.location.pathname + window.location.search;

    if (currentPath.startsWith('/login') ||
      currentPath.startsWith('/auth') ||
      currentPath.startsWith('/terms-of-service') ||
      currentPath.startsWith('/privacy-policy')) {
      return;
    }

    this.authToken = null;
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('token');

    const loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;

    toast.error('Your session has expired. Please log in again.');

    window.location.href = loginUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const config: RequestInit = {
      headers,
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401 && this.refreshCallback) {
        if (!this.isRefreshing) {
          this.isRefreshing = true;
          try {
            await this.refreshCallback();
            if (this.authToken) {
              return Promise.all(this.failedQueue.map(cb => cb(this.authToken))).then(() => {
                headers['Authorization'] = `Bearer ${this.authToken}`;
                return fetch(url, { ...config, headers }).then(res => res.json());
              });
            } else {
              this.redirectToLogin();
              throw new Error('Authentication failed');
            }
          } catch (refreshError) {
            this.redirectToLogin();
            throw new Error('Authentication failed');
          } finally {
            this.isRefreshing = false;
          }
        }


        return new Promise((resolve, reject) => {
          this.subscribeTokenRefresh(() => {
            headers['Authorization'] = `Bearer ${this.authToken}`;
            fetch(url, { ...config, headers })
              .then(response => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
              })
              .then(resolve)
              .catch(reject);
          });
        });
      }

      if (response.status === 429) {
        throw notifyRateLimited(response);
      }

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
        
        }



        if (response.status === 401) {
          toast.error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          toast.error(errorMessage??'You do not have permission to perform this action.');
        } else if (response.status === 404) {
          toast.error(errorMessage ? errorMessage : 'Resource not found.');
        } else if (response.status === 409) {
          toast.error(errorMessage);
        } else if (response.status >= 500) {
          toast.error('Server error. Please try again later.');
        } else {
          toast.error(errorMessage);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
      }

      return data;
    } catch (error) {

   

      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<APIResponse<T>> {
    const url = new URL(endpoint, this.baseURL);
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key].toString());
        }
      });
    }

    return this.request<T>(url.pathname + url.search);
  }

  async post<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async download<T>(endpoint: string, params?: Record<string, any>): Promise<ArrayBuffer> {
    const headers: Record<string, string> = {};

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const config: RequestInit = {
      method: 'GET',
      headers,
    };

    const url = `${this.baseURL}${endpoint}`;

    let response = await fetch(url, config);

    if (response.status === 401 && this.refreshCallback && !this.isRefreshing) {
      this.isRefreshing = true;
      try {
        await this.refreshCallback();
        if (this.authToken) {
          headers['Authorization'] = `Bearer ${this.authToken}`;
          response = await fetch(url, { ...config, headers });
        } else {
          this.redirectToLogin();
          throw new Error('Authentication failed');
        }
      } catch (refreshError) {
        this.redirectToLogin();
        throw new Error('Authentication failed');
      } finally {
        this.isRefreshing = false;
      }
    }

    if (response.status === 429) {
      throw notifyRateLimited(response);
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.arrayBuffer();
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<APIResponse<T>> {
    const headers: Record<string, string> = {};

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    const config: RequestInit = {
      method: 'POST',
      headers,
      body: formData,
    };

    const url = `${this.baseURL}${endpoint}`;

    try {
      let response = await fetch(url, config);

      if (response.status === 401 && this.refreshCallback && !this.isRefreshing) {
        this.isRefreshing = true;
        try {
          await this.refreshCallback();
          if (this.authToken) {
            headers['Authorization'] = `Bearer ${this.authToken}`;
            response = await fetch(url, { ...config, headers });
          } else {
            this.redirectToLogin();
            throw new Error('Authentication failed');
          }
        } catch (refreshError) {
          this.redirectToLogin();
          throw new Error('Authentication failed');
        } finally {
          this.isRefreshing = false;
        }
      }


      if (response.status === 429) {
        throw notifyRateLimited(response);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
}

export const apiClient = new ApiClient();
